import { useEffect, useReducer } from 'react';
import type { Editor } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatCurrencyRange } from '@/lib/format';
import { applyInvestmentGroupLabelsLanguage } from '@/editor/applyInvestmentGroupLabels';
import {
  addInvestmentRowToPhase,
  investmentDestinations,
  investmentGroupForPage,
  investmentPhaseTotal,
} from '@/editor/quoteWorkspace';
import { useActiveEditor } from '@/store/editorContext';
import { InvestmentInspectorBody } from './InvestmentInspector';
import InvestmentPhaseRowsEditor from './InvestmentPhaseRowsEditor';
import { InvestmentRateCatalogProvider } from './InvestmentRateSelect';
import InvestmentOverviewsList from './InvestmentOverviewsList';
import {
  useInvestmentInspectorStore,
  type InvestmentInspectorPhase,
} from '@/store/investmentInspectorStore';

export default function InvestmentPageOverview({
  editor,
  blockId,
  onDocumentMode,
  activePhase = null,
}: {
  editor: Editor;
  blockId: string;
  onDocumentMode: () => void;
  activePhase?: InvestmentInspectorPhase | null;
}) {
  const { setProductPickerTableId } = useActiveEditor();
  const groupSettings = useInvestmentInspectorStore((state) => state.group);
  const [, refresh] = useReducer((value) => value + 1, 0);
  useEffect(() => {
    editor.on('update', refresh);
    return () => {
      editor.off('update', refresh);
    };
  }, [editor]);
  const group = investmentGroupForPage(editor.state.doc, blockId);
  if (!group) return null;
  const headers: Array<{ node: PMNode; pos: number }> = [];
  group.node.forEach((node, offset) => {
    if (node.type.name === 'investmentHeaderNode' && node.attrs.isMainHeader)
      headers.push({ node, pos: group.pos + 1 + offset });
  });
  const mainHeader = headers[0];
  const currency = String(group.node.attrs.currency || 'EUR');
  const phases = investmentDestinations(editor.state.doc).filter(
    (phase) => phase.groupId === blockId && (!activePhase || phase.tableId === activePhase.tableId)
  );
  return (
    <div className="workspace-investment-overview" data-investment-page-overview>
      <div className="workspace-overview-title">
        <h3>{activePhase?.title || 'Investering'}</h3>
      </div>
      {!activePhase && (
        <div className="workspace-overview-settings">
          {mainHeader && (
            <label>
              Paginatitel in offerte
              <input
                className="workspace-input"
                value={mainHeader.node.attrs.mainTitle || ''}
                onChange={(event) =>
                  editor.view.dispatch(
                    editor.state.tr.setNodeMarkup(mainHeader.pos, undefined, {
                      ...mainHeader.node.attrs,
                      mainTitle: event.target.value,
                    })
                  )
                }
              />
            </label>
          )}
          <label>
            Taal
            <Select
              value={group.node.attrs.labelLanguage || 'nl'}
              onValueChange={(value) =>
                applyInvestmentGroupLabelsLanguage(editor, group.pos, value === 'en' ? 'en' : 'nl')
              }
            >
              <SelectTrigger className="h-9 bg-card text-xs font-normal">
                <SelectValue />
              </SelectTrigger>
              <SelectContent data-investment-inspector>
                <SelectItem value="nl">Nederlands</SelectItem>
                <SelectItem value="en">English</SelectItem>
              </SelectContent>
            </Select>
          </label>
          <label>
            Valuta
            <Select
              value={currency}
              onValueChange={(value) =>
                editor.view.dispatch(
                  editor.state.tr.setNodeMarkup(group.pos, undefined, {
                    ...group.node.attrs,
                    currency: value,
                  })
                )
              }
            >
              <SelectTrigger className="h-9 bg-card text-xs font-normal">
                <SelectValue />
              </SelectTrigger>
              <SelectContent data-investment-inspector>
                <SelectItem value="EUR">Euro (€)</SelectItem>
                <SelectItem value="GBP">Pond (£)</SelectItem>
                <SelectItem value="USD">Dollar ($)</SelectItem>
              </SelectContent>
            </Select>
          </label>
        </div>
      )}
      {!activePhase && (
        <InvestmentOverviewsList key={blockId} editor={editor} blockId={blockId} phases={phases} />
      )}
      {activePhase && (
        <InvestmentRateCatalogProvider>
          {phases.map((phase) => {
            const total = investmentPhaseTotal(editor.state.doc, phase);
            return (
              <section key={phase.tableId} className="workspace-overview-phase">
                <div className="workspace-overview-phase-heading">
                  <button
                    className="workspace-quiet-button"
                    type="button"
                    onClick={() => setProductPickerTableId(phase.tableId)}
                  >
                    <Plus size={14} />
                    Product toevoegen
                  </button>
                  <button
                    className="workspace-quiet-button"
                    type="button"
                    onClick={() => addInvestmentRowToPhase(editor, phase.tableId)}
                  >
                    <Plus size={14} />
                    Regel toevoegen
                  </button>
                </div>
                <InvestmentPhaseRowsEditor editor={editor} destination={phase} />
                <div className="workspace-overview-total">
                  <span>Totaal fase · excl. btw</span>
                  <strong>
                    {total.text ||
                      formatCurrencyRange(total.amount / 100, total.maxAmount / 100, currency)}
                    {total.hasOnRequest ? ' + op aanvraag' : ''}
                  </strong>
                </div>
              </section>
            );
          })}
        </InvestmentRateCatalogProvider>
      )}
      {activePhase && (
        <details className="workspace-phase-settings">
          <summary>Tabelinstellingen en acties</summary>
          <InvestmentInspectorBody
            group={groupSettings}
            phase={activePhase}
            showSelectedRow={false}
          />
        </details>
      )}
      {activePhase && (
        <p className="workspace-overview-hint">
          Bewerk losse regels direct in deze lijst. Klik op een product om de prijsopbouw te
          bewerken.
        </p>
      )}
      <Button variant="outline" size="sm" onClick={onDocumentMode}>
        Documentweergave openen
      </Button>
    </div>
  );
}
